import type { CSSProperties } from 'react'
import { cn } from '@/lib/utils'
import { CountUp, StaggerContainer, StaggerItem } from '@/components/motion'

export interface Stat {
  value: string
  label: string
}

/** Plain numeric metrics ("95%", "3x", "-95%") count up once; anything else renders as-is. */
function Metric({ value }: { value: string }) {
  const match = /^(\D{0,2})(\d{1,3}(?:,\d{3})*|\d+)(\D{0,4})$/.exec(value.trim())
  if (!match) return <>{value}</>
  const numeric = Number(match[2]!.replace(/,/g, ''))
  if (!Number.isFinite(numeric)) return <>{value}</>
  return <CountUp value={numeric} prefix={match[1]} suffix={match[3]} />
}

/**
 * Three stats on a coloured ground (white text): a big Boldonse figure over
 * a block with a 2px left rule holding the label. `size="hero"` is the
 * h1-size row inside the Solutions hero; `size="display"` the larger figures
 * of the Platform stats band. The figure size scales with the longest value
 * so the row stays one size and every figure fits its column.
 */
export function StatsRow({
  stats,
  size = 'hero',
  immediate = false,
  className,
}: {
  stats: Stat[]
  size?: 'hero' | 'display'
  /** Reveal on mount (above the fold) instead of on scroll. */
  immediate?: boolean
  className?: string
}) {
  // Size the figures by the longest value so Boldonse's wide letters stay
  // inside their column: about 0.95em per character.
  const longest = Math.max(1, ...stats.map((s) => s.value.length))
  const figure =
    size === 'display'
      ? 'text-[clamp(2.5rem,calc(64vw/var(--len)),6rem)] sm:text-[clamp(2.25rem,calc(22vw/var(--len)),8.5rem)]'
      : 'text-[clamp(2.5rem,calc(64vw/var(--len)),5rem)] sm:text-[clamp(2rem,calc(20vw/var(--len)),5.5rem)]'

  return (
    <StaggerContainer
      immediate={immediate}
      delayChildren={immediate ? 0.35 : 0}
      className={cn('grid gap-x-10 gap-y-12 sm:grid-cols-3', className)}
      style={{ '--len': longest } as CSSProperties}
    >
      {stats.map((stat) => (
        <StaggerItem key={stat.label} className="min-w-0">
          <p
            className={cn(
              'whitespace-nowrap font-[family-name:var(--font-landing-display)] uppercase leading-[1.25] tracking-[-0.02em] text-white tabular-nums',
              figure
            )}
          >
            <Metric value={stat.value} />
          </p>
          <p className="mt-4 border-l-2 border-white/70 pl-4 text-lg font-bold uppercase leading-snug tracking-[0.01em] text-white md:text-xl">
            {stat.label}
          </p>
        </StaggerItem>
      ))}
    </StaggerContainer>
  )
}

export default StatsRow
